import { CSSProperties, ReactNode, useCallback, useEffect, useRef, useState } from 'react'
import CircularProgress from '@mui/material/CircularProgress'
import { useRefresh } from '../../contexts/RefreshContext'
import { IMMERSIVE_ATTRIBUTE } from '../../hooks/useImmersiveSurface'
import { useIsMobile } from '../../hooks/useIsMobile'
import { LONG_PULL_ATTRIBUTE, PULL_SCROLL_PANE_ATTRIBUTE } from '../../hooks/useLongPullToRefresh'
import { KEYBOARD_OPEN_ATTRIBUTE } from '../../hooks/useVisualViewport'
import styles from './PullToRefresh.module.scss'

/**
 * Pull down at the top of a page to refetch it.
 *
 * Hand-built rather than left to the browser, because neither half of the platform
 * offers it here: `body { overscroll-behavior-y: none }` turns the native gesture off
 * on purpose (rubber-banding the document exposes the page background above the fixed
 * header and below the tab bar), and the manifest is `display: standalone`, so an
 * installed Atlas has no browser chrome to pull against in the first place. The
 * affordance people expect from every other phone app therefore has to be drawn.
 *
 * What it refreshes is whatever the current page registered through `useRefreshHandler`;
 * with nothing registered it reloads the document. See contexts/RefreshContext.tsx.
 *
 * How far the pull has to come is per-surface. A page that scrolls the document gets
 * `TRIGGER_DISTANCE`; a surface whose content scrolls inside a pane of its own can ask for
 * the longer `LONG_TRIGGER_DISTANCE` through `useLongPullToRefresh`, because the document
 * scroll this reads says nothing about how far down such a surface the user actually is.
 */

/** Drag past this to arm the refresh. Roughly a thumb's travel, and clear of a stray swipe. */
const TRIGGER_DISTANCE = 72

/**
 * The threshold on a surface that asked for the heavier pull (useLongPullToRefresh).
 *
 * Half again as far, which at `DRAG_RESISTANCE` is a little over 200px of finger — past
 * anything a flick or a glancing swipe produces, and still comfortably inside one thumb
 * stroke on a phone, so a pull meant as a pull still lands first time.
 *
 * The surfaces that ask for it are the ones where the `scrollY === 0` guard below cannot
 * tell a pull from a scroll because their content scrolls inside its own pane rather than
 * the document. See hooks/useLongPullToRefresh.ts.
 */
const LONG_TRIGGER_DISTANCE = 108

/**
 * How far past the threshold the indicator may still travel, so an enthusiastic pull
 * has somewhere to go instead of stopping dead the moment it arms. Added to whichever
 * threshold is in force, which keeps the two modes feeling like one gesture.
 */
const OVERPULL_ALLOWANCE = 38

/**
 * Fraction of finger travel the indicator actually moves. The pull is deliberately
 * heavier than 1:1 — the resistance is what tells you it is a gesture with a
 * threshold rather than a surface that scrolls.
 */
const DRAG_RESISTANCE = 0.5

/** Below this, treat the drag as a tap or a horizontal swipe and ignore it. */
const DRAG_DEADZONE = 8

/** True for a box that scrolls its own overflow and is not currently at the top of it. */
const isScrolledPane = (element: Element): boolean => {
  // Cheap test first: all but a handful of elements in the chain are at 0, and reading
  // computed styles for every ancestor of every touch is not free.
  if (element.scrollTop <= 0) {
    return false
  }
  const { overflowY } = window.getComputedStyle(element)
  return overflowY === 'auto' || overflowY === 'scroll'
}

/**
 * Whether the finger landed inside a pane that is scrolled away from its own top.
 *
 * `window.scrollY` only speaks for pages that scroll the document. A surface that scrolls
 * inside a pane of its own — AIM's conversation list, any `overflow-y: auto` column —
 * leaves the document at 0 no matter how far down its content the user is, so without this
 * a swipe back up a long list reads as a pull at the top of the page and refreshes. Walking
 * up from the touch target answers the question the document scroll cannot.
 *
 * Stops at `<body>`: the document's own scroll is the check this complements, not part of it.
 *
 * This is not AIM-only, and Notifications is the surface it changes: `.notificationsList` is
 * `overflow-y: auto` under a `max-height` (NotificationPage.module.scss), and `.content` is
 * `overflow: visible` below `md` (MainLayout.module.scss), so that list scrolls in its own
 * pane while `window.scrollY` stays 0. A pull started partway down it used to refresh and now
 * does not. That is deliberate — you pull from the top of a list, as every other phone app
 * does — but it is a real change to the one surface with a registered handler, not a no-op.
 */
const hasScrolledPane = (target: EventTarget | null): boolean => {
  let element = target instanceof Element ? target : null
  while (element && element !== document.body && element !== document.documentElement) {
    if (isScrolledPane(element)) {
      return true
    }
    element = element.parentElement
  }
  return false
}

/**
 * Whether a surface that reported its own scroll pane is scrolled away from that pane's top.
 *
 * `hasScrolledPane` above walks up from the touch target, so it only answers for a finger
 * that landed inside the pane. AIM's sidebar header, which carries the search field, is a
 * sibling above `.sidebarScroll`, and a pull starting on it walks up to `#root`
 * finding nothing scrolled however far down the conversation list is. A surface that knows
 * where its content scrolls says so through useLongPullToRefresh, and the guard stops
 * depending on where in the surface the touch happened to land.
 *
 * No `getComputedStyle` here, unlike `isScrolledPane`: a reported pane has already declared
 * itself to be the thing that scrolls, so its `scrollTop` is the whole question.
 */
const hasScrolledReportedPane = (): boolean =>
  Array.from(document.querySelectorAll(`[${PULL_SCROLL_PANE_ATTRIBUTE}]`)).some(
    (pane) => pane.scrollTop > 0,
  )

const PullToRefresh = ({ children }: { children: ReactNode }) => {
  const isMobile = useIsMobile()
  const { runRefresh, hasHandler } = useRefresh()

  const [pullDistance, setPullDistance] = useState(0)
  const [isRefreshing, setIsRefreshing] = useState(false)
  /**
   * The threshold this gesture has to clear, fixed at touchstart so it cannot change
   * under a pull already in progress. State as well as a ref because the indicator's
   * ring fills against it; it changes once per surface, not once per touchmove.
   */
  const [triggerDistance, setTriggerDistance] = useState(TRIGGER_DISTANCE)

  // Refs, not state: these are written on every touchmove, and re-rendering the whole
  // page subtree to remember where a finger started would make the gesture stutter.
  const startYRef = useRef<number | null>(null)
  /**
   * Where the finger went down horizontally, and whether this gesture has already been
   * ruled out as a sideways one.
   *
   * The pull only ever measured `clientY`, so a drag with any downward component armed it
   * — including the ones that are plainly something else. The app is full of those: the
   * financial tables scroll sideways, and AIM now puts swipe actions on its list rows and
   * an edge swipe on its panes. A thumb arcs as it travels, so a swipe across a row would
   * creep the indicator down the screen while the row moved under it.
   */
  const startXRef = useRef<number | null>(null)
  const isRefreshingRef = useRef(false)
  /**
   * How far the pull has come, for the handlers to read.
   *
   * The state below still drives the indicator; this mirrors it because
   * `handleTouchEnd` needs the distance to decide whether the gesture armed, and
   * reading the state there put `pullDistance` in that callback's dependencies. The
   * registration effect depends on the callback, so a new value every touchmove tore
   * down and re-added four document-level touch listeners — at ~60fps, for the whole
   * length of the pull. That listener churn was the lag in the gesture.
   */
  const pullDistanceRef = useRef(0)
  /** The mirror of `triggerDistance`, for the same reason. */
  const triggerDistanceRef = useRef(TRIGGER_DISTANCE)

  const setPull = useCallback((distance: number) => {
    pullDistanceRef.current = distance
    setPullDistance(distance)
  }, [])

  const canPull = useCallback(() => {
    if (!isMobile || isRefreshingRef.current) {
      return false
    }
    // The document has to be at the very top, or the pull is just a scroll.
    if (window.scrollY > 0) {
      return false
    }
    // An immersive surface owns the screen and scrolls inside its own panes, so the
    // document scroll this gesture reads is meaningless there. The keyboard case is
    // worse than meaningless: iOS shifts the visual viewport to reveal the input, and
    // a refresh mid-typing would throw away what is being composed.
    const root = document.documentElement
    return !root.hasAttribute(IMMERSIVE_ATTRIBUTE) && !root.hasAttribute(KEYBOARD_OPEN_ATTRIBUTE)
  }, [isMobile])

  const handleTouchStart = useCallback(
    (event: TouchEvent) => {
      // Multi-touch is a pinch-zoom, not a pull; and a finger that lands inside a pane
      // scrolled down its own content — or anywhere on a surface whose reported pane is
      // scrolled down — is there to scroll it back up.
      const isPull =
        canPull() &&
        event.touches.length === 1 &&
        !hasScrolledPane(event.target) &&
        !hasScrolledReportedPane()
      startYRef.current = isPull ? event.touches[0].clientY : null
      startXRef.current = isPull ? event.touches[0].clientX : null
      if (!isPull) {
        return
      }
      const distance = document.documentElement.hasAttribute(LONG_PULL_ATTRIBUTE)
        ? LONG_TRIGGER_DISTANCE
        : TRIGGER_DISTANCE
      triggerDistanceRef.current = distance
      setTriggerDistance(distance)
    },
    [canPull],
  )

  const handleTouchMove = useCallback(
    (event: TouchEvent) => {
      const startY = startYRef.current
      if (startY === null) {
        return
      }
      // Re-checked mid-drag, not just at touchstart: the finger may have scrolled the
      // page down before reversing, and that upward travel is a scroll, not a pull.
      if (window.scrollY > 0) {
        startYRef.current = null
        setPull(0)
        return
      }
      const travel = event.touches[0].clientY - startY
      const sideways = Math.abs(event.touches[0].clientX - (startXRef.current ?? 0))
      // Whichever axis leads owns the gesture, and once it is the horizontal one this
      // pull is out for the rest of the touch rather than re-arming as the arc comes
      // back down. Same rule the swipe gestures use against the scrollers beneath them.
      if (sideways > DRAG_DEADZONE && sideways > travel) {
        startYRef.current = null
        setPull(0)
        return
      }
      if (travel < DRAG_DEADZONE) {
        setPull(0)
        return
      }
      const maxPull = triggerDistanceRef.current + OVERPULL_ALLOWANCE
      setPull(Math.min(travel * DRAG_RESISTANCE, maxPull))
    },
    [setPull],
  )

  const handleTouchEnd = useCallback(() => {
    const shouldRefresh =
      startYRef.current !== null && pullDistanceRef.current >= triggerDistanceRef.current
    startYRef.current = null
    startXRef.current = null

    if (!shouldRefresh) {
      setPull(0)
      return
    }

    // A page with no handler still owes the user something for the gesture, and a
    // reload is the one refresh that is correct on every screen.
    if (!hasHandler()) {
      window.location.reload()
      return
    }

    isRefreshingRef.current = true
    setIsRefreshing(true)
    setPull(triggerDistanceRef.current)

    void runRefresh()
      .catch(() => {
        // A failed refetch surfaces through the page's own error handling; all this
        // gesture owes is to stop spinning rather than hang on a rejected promise.
      })
      .finally(() => {
        isRefreshingRef.current = false
        setIsRefreshing(false)
        setPull(0)
      })
  }, [setPull, hasHandler, runRefresh])

  useEffect(() => {
    if (!isMobile) {
      return
    }
    // Passive: the gesture only runs at `scrollY === 0`, where a downward drag has
    // nothing left to scroll, so there is no default worth cancelling — and a
    // non-passive touchmove listener on the document costs scroll performance
    // everywhere else.
    const options: AddEventListenerOptions = { passive: true }
    document.addEventListener('touchstart', handleTouchStart, options)
    document.addEventListener('touchmove', handleTouchMove, options)
    document.addEventListener('touchend', handleTouchEnd, options)
    document.addEventListener('touchcancel', handleTouchEnd, options)
    return () => {
      document.removeEventListener('touchstart', handleTouchStart)
      document.removeEventListener('touchmove', handleTouchMove)
      document.removeEventListener('touchend', handleTouchEnd)
      document.removeEventListener('touchcancel', handleTouchEnd)
    }
  }, [isMobile, handleTouchStart, handleTouchMove, handleTouchEnd])

  const isVisible = pullDistance > 0 || isRefreshing
  const isArmed = pullDistance >= triggerDistance

  return (
    <>
      {isMobile && isVisible && (
        <div
          className={styles.indicator}
          style={{ '--pull-distance': pullDistance } as CSSProperties}
          // Announced rather than drawn only: a spinner that appears on a gesture is
          // invisible to a screen reader, which has no way to have performed the pull.
          role="status"
          aria-live="polite"
        >
          <CircularProgress
            size={20}
            thickness={4}
            // Determinate while the finger is down, so the ring fills as the threshold
            // approaches and the gesture says how much further to go.
            variant={isRefreshing ? 'indeterminate' : 'determinate'}
            value={Math.min((pullDistance / triggerDistance) * 100, 100)}
            className={isArmed ? styles.armed : undefined}
          />
        </div>
      )}
      {children}
    </>
  )
}

export default PullToRefresh
